// ADC-IMPLEMENTS: <adc-006a-algo-display-01>
// ADC-IMPLEMENTS: <adc-006a-dm-incident-frontend-01>
/**
 * Incident display helpers and type definitions for ADC-006A Incident Management Frontend UI.
 *
 * This module provides:
 * - Frontend TypeScript interfaces mirroring Firestore incident documents
 * - Firestore-to-frontend mapping with dual timestamp format support
 * - Status/severity color configuration for consistent rendering
 * - Shared utility functions (getScoreColorClass extracted from ConversationView)
 */

import { formatDistanceToNow } from 'date-fns';
import type { DocumentData } from 'firebase/firestore';

// ── Types ──────────────────────────────────────────────────────────────────────

export type IncidentStatus = 'PENDING' | 'ACTIVE' | 'ESCALATED' | 'RESOLVED' | 'FALSE_POSITIVE';
export type IncidentSeverity = 'low' | 'medium' | 'high' | 'critical';
export type FirebaseStatus = 'active' | 'closed';

// ADC-006B F-11: Message content types
export type MessageType = 'text' | 'image' | 'video' | 'audio' | 'document' | 'sticker';

export interface IncidentMessage {
  message_id: string;
  timestamp: Date;
  flagged_categories: string[];
  severity_score: number;
  // ADC-006B F-11: Content fields (optional for backward compatibility)
  content: string;
  messageType: MessageType;
  pushName: string;
  fromMe: boolean;
  mediaUrl: string;
}

export interface IncidentContext {
  conversation_name: string;
  reasoning: string;
  confidence: number;
  flagged_reasons: string[];
  moderation_categories: Record<string, number>;
  message_enrichment: {
    fromMe: boolean;
    pushName: string;
    messageType: string;
  };
}

export interface NarrativePhase {
  number: number;
  title: string;
  description: string;
  key_participants: string[];
}

export interface NarrativeParticipant {
  name: string;
  role: 'instigator' | 'victim' | 'bystander' | 'intervener' | string;
  message_count: number;
  key_actions: string[];
}

export interface IncidentNarrative {
  summary: string;
  phases: NarrativePhase[];
  participants: NarrativeParticipant[];
  severity_assessment: string;
  recommended_actions: string[];
  resolution_detected: boolean;
  resolution_type: string | null;
  generated_at: Date | null;
  message_count_at_generation: number;
}

export interface TimelineMessage {
  message_id: string;
  timestamp: Date;
  message_timestamp: number | null;
  content: string;
  messageType: string;
  pushName: string;
  fromMe: boolean;
  context_type: 'evidence' | 'context';
  severity_score: number;
  flagged_categories: string[];
  mediaUrl: string;
  event_explanation: string;
}

export interface Incident {
  id: string;                    // Firestore document ID
  incident_id: string;           // Generated UUID from backend
  conversation_id: string;
  connection_id: string;
  status: IncidentStatus;
  firebase_status: FirebaseStatus;
  severity: IncidentSeverity;
  severity_score: number;
  created_at: Date;
  updated_at: Date;
  closed_at: Date | null;
  flagged_message_id: string;
  message_count: number;
  context_message_count: number;
  last_message_id: string;
  messages: IncidentMessage[];
  context: IncidentContext;
  narrative: IncidentNarrative | null;
  resolution_notes: string;
  escalated_to: string;
}

// ── Timestamp Parsing ──────────────────────────────────────────────────────────

/**
 * Parse a Firestore timestamp field that may be either:
 * - A Firestore Timestamp object (has .toDate() method) -- target format after backend migration
 * - An ISO 8601 string (e.g., "2026-03-04T15:30:00+00:00") -- legacy backend format
 * - null/undefined -- returns the provided fallback
 *
 * ADC-006A v1.2.0: Handles both formats for backward compatibility during
 * backend migration from ISO strings to firestore.SERVER_TIMESTAMP.
 */
export function parseTimestamp(value: unknown, fallback: Date | null = new Date()): Date | null {
  if (value == null) return fallback;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? (fallback ?? new Date()) : parsed;
  }
  if (value instanceof Date) {
    return value;
  }
  return fallback;
}

// ── Severity Score to Label ───────────────────────────────────────────────────

/**
 * Convert numeric severity (0-10) to label. Mirrors backend severity_score_to_label().
 * Used when Firestore `severity` field is stored as a number instead of a label string.
 */
function severityScoreToLabel(score: number): IncidentSeverity {
  if (score < 3.0) return 'low';
  if (score < 5.0) return 'medium';
  if (score < 7.0) return 'high';
  return 'critical';
}

/**
 * Normalize the `severity` field which may be a label string, a numeric score,
 * or missing entirely. Handles both pipeline formats and retroactive incidents.
 */
function normalizeSeverity(raw: unknown, severityScore: number): IncidentSeverity {
  if (typeof raw === 'string' && raw in SEVERITY_CONFIG) {
    return raw as IncidentSeverity;
  }
  if (typeof raw === 'number') {
    return severityScoreToLabel(raw);
  }
  // Fallback: derive from severity_score
  return severityScoreToLabel(severityScore);
}

// ── Firestore Mapping ──────────────────────────────────────────────────────────

export function mapFirestoreToIncident(docId: string, data: DocumentData): Incident {
  const severityScore = typeof data.severity_score === 'number' ? data.severity_score : 0;
  return {
    id: docId,
    incident_id: data.incident_id ?? docId,
    conversation_id: data.conversation_id ?? '',
    connection_id: data.connection_id ?? '',
    status: data.status ?? 'PENDING',
    firebase_status: data.firebase_status ?? 'active',
    severity: normalizeSeverity(data.severity, severityScore),
    severity_score: severityScore,
    created_at: parseTimestamp(data.created_at ?? data.createdAt, new Date())!,
    updated_at: parseTimestamp(data.updated_at ?? data.updatedAt, new Date())!,
    closed_at: parseTimestamp(data.closed_at, null),
    flagged_message_id: data.flagged_message_id ?? '',
    message_count: data.message_count ?? 0,
    context_message_count: data.context_message_count ?? 0,
    last_message_id: data.last_message_id ?? '',
    messages: (data.messages ?? []).map((m: Record<string, unknown>) => ({
      message_id: (m.message_id as string) ?? '',
      timestamp: parseTimestamp(m.timestamp, new Date())!,
      flagged_categories: (m.flagged_categories as string[]) ?? [],
      severity_score: (m.severity_score as number) ?? 0,
      // ADC-006B F-11: Content fields with backward-compatible defaults
      content: (m.content as string) ?? '',
      messageType: ((m.messageType as string) ?? 'text') as MessageType,
      pushName: (m.pushName as string) ?? 'Unknown',
      fromMe: (m.fromMe as boolean) ?? false,
      mediaUrl: (m.mediaUrl as string) ?? '',
    })),
    context: {
      conversation_name: data.context?.conversation_name ?? '',
      reasoning: data.context?.reasoning ?? '',
      confidence: data.context?.confidence ?? 0,
      flagged_reasons: data.context?.flagged_reasons ?? [],
      moderation_categories: data.context?.moderation_categories ?? {},
      message_enrichment: {
        fromMe: data.context?.message_enrichment?.fromMe ?? false,
        pushName: data.context?.message_enrichment?.pushName ?? '',
        messageType: data.context?.message_enrichment?.messageType ?? '',
      },
    },
    narrative: data.narrative ? {
      summary: data.narrative.summary ?? '',
      phases: (data.narrative.phases ?? []).map((p: Record<string, unknown>) => ({
        number: (p.number as number) ?? 0,
        title: (p.title as string) ?? '',
        description: (p.description as string) ?? '',
        key_participants: (p.key_participants as string[]) ?? [],
      })),
      participants: (data.narrative.participants ?? []).map((p: Record<string, unknown>) => ({
        name: (p.name as string) ?? '',
        role: (p.role as string) ?? 'bystander',
        message_count: (p.message_count as number) ?? 0,
        key_actions: (p.key_actions as string[]) ?? [],
      })),
      severity_assessment: data.narrative.severity_assessment ?? '',
      recommended_actions: data.narrative.recommended_actions ?? [],
      resolution_detected: data.narrative.resolution_detected ?? false,
      resolution_type: data.narrative.resolution_type ?? null,
      generated_at: parseTimestamp(data.narrative.generated_at, null),
      message_count_at_generation: data.narrative.message_count_at_generation ?? 0,
    } : null,
    resolution_notes: data.resolution_notes ?? '',
    escalated_to: data.escalated_to ?? '',
  };
}

// ── Display Configuration ──────────────────────────────────────────────────────

export const STATUS_CONFIG: Record<IncidentStatus, { label: string; className: string }> = {
  PENDING:        { label: 'Pending',         className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  ACTIVE:         { label: 'Active',          className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' },
  ESCALATED:      { label: 'Escalated',       className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  RESOLVED:       { label: 'Resolved',        className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  FALSE_POSITIVE: { label: 'False Positive',  className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' },
};

export const SEVERITY_CONFIG: Record<IncidentSeverity, { label: string; className: string; dotColor: string }> = {
  low:      { label: 'Low',      className: 'bg-blue-500 text-white',   dotColor: 'bg-blue-500' },
  medium:   { label: 'Medium',   className: 'bg-yellow-500 text-black', dotColor: 'bg-yellow-500' },
  high:     { label: 'High',     className: 'bg-red-500 text-white',    dotColor: 'bg-red-500' },
  critical: { label: 'Critical', className: 'bg-red-600 text-white',    dotColor: 'bg-red-600' },
};

export function getStatusConfig(status: IncidentStatus): { label: string; className: string } {
  return STATUS_CONFIG[status] ?? STATUS_CONFIG.PENDING;
}

export function getSeverityConfig(severity: IncidentSeverity): { label: string; className: string; dotColor: string } {
  return SEVERITY_CONFIG[severity] ?? SEVERITY_CONFIG.low;
}

export function isTerminalStatus(status: IncidentStatus): boolean {
  return status === 'RESOLVED' || status === 'FALSE_POSITIVE';
}

export function formatIncidentAge(createdAt: Date): string {
  return formatDistanceToNow(createdAt, { addSuffix: true });
}

// F12: Extracted from ConversationView.tsx into shared util
export function getScoreColorClass(score: number): string {
  if (score >= 0.8) return "bg-destructive/20 text-destructive dark:text-destructive-foreground";
  if (score >= 0.5) return "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-900 dark:text-yellow-300";
  return "bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-300";
}

// ── Dashboard Aggregation (v2.0.0) ───────────────────────────────────────────
// ADC-IMPLEMENTS: <adc-006a-dm-dashboard-01>

export interface IncidentSummary {
  total: number;
  bySeverity: Record<IncidentSeverity, number>;
  byStatus: Record<IncidentStatus, number>;
}

export interface IncidentFilters {
  status: IncidentStatus | 'ALL';
  severity: IncidentSeverity | 'ALL';
  connectionId: string | 'ALL';
  searchQuery: string;
}

export const DEFAULT_INCIDENT_FILTERS: IncidentFilters = {
  status: 'ALL',
  severity: 'ALL',
  connectionId: 'ALL',
  searchQuery: '',
};

export interface SeverityGroup {
  severity: IncidentSeverity;
  incidents: Incident[];
  count: number;
}

export function computeIncidentSummary(incidents: Incident[]): IncidentSummary {
  const summary: IncidentSummary = {
    total: incidents.length,
    bySeverity: { critical: 0, high: 0, medium: 0, low: 0 },
    byStatus: { PENDING: 0, ACTIVE: 0, ESCALATED: 0, RESOLVED: 0, FALSE_POSITIVE: 0 },
  };
  for (const inc of incidents) {
    summary.bySeverity[inc.severity]++;
    summary.byStatus[inc.status]++;
  }
  return summary;
}

export function groupBySeverity(incidents: Incident[]): SeverityGroup[] {
  const order: IncidentSeverity[] = ['critical', 'high', 'medium', 'low'];
  return order
    .map(sev => ({
      severity: sev,
      incidents: incidents
        .filter(i => i.severity === sev)
        .sort((a, b) => b.created_at.getTime() - a.created_at.getTime()),
      count: incidents.filter(i => i.severity === sev).length,
    }))
    .filter(g => g.count > 0);
}

// ADC-006B F-09: Conversation grouping
export interface ConversationGroup {
  conversation_id: string;
  conversation_name: string;
  connection_id: string;
  incidents: Incident[];
  highest_severity: IncidentSeverity;
  incident_count: number;
}

const SEVERITY_ORDER: Record<IncidentSeverity, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

export function groupByConversation(incidents: Incident[]): ConversationGroup[] {
  const groups: Record<string, ConversationGroup> = {};
  for (const incident of incidents) {
    const key = incident.conversation_id;
    if (!groups[key]) {
      groups[key] = {
        conversation_id: key,
        conversation_name: incident.context.conversation_name || 'Unknown Conversation',
        connection_id: incident.connection_id,
        incidents: [],
        highest_severity: 'low',
        incident_count: 0,
      };
    }
    groups[key].incidents.push(incident);
  }

  return Object.values(groups)
    .map(group => {
      group.incidents.sort((a, b) => {
        const sevDiff = SEVERITY_ORDER[b.severity] - SEVERITY_ORDER[a.severity];
        if (sevDiff !== 0) return sevDiff;
        return b.created_at.getTime() - a.created_at.getTime();
      });
      group.highest_severity = group.incidents[0].severity;
      group.incident_count = group.incidents.length;
      return group;
    })
    .sort((a, b) => {
      const sevDiff = SEVERITY_ORDER[b.highest_severity] - SEVERITY_ORDER[a.highest_severity];
      if (sevDiff !== 0) return sevDiff;
      return b.incident_count - a.incident_count;
    });
}

export function applyIncidentFilters(incidents: Incident[], filters: IncidentFilters): Incident[] {
  return incidents.filter(inc => {
    if (filters.status !== 'ALL' && inc.status !== filters.status) return false;
    if (filters.severity !== 'ALL' && inc.severity !== filters.severity) return false;
    if (filters.connectionId !== 'ALL' && inc.connection_id !== filters.connectionId) return false;
    if (filters.searchQuery) {
      const q = filters.searchQuery.toLowerCase();
      const searchable = [
        inc.context.reasoning,
        inc.context.conversation_name,
        inc.incident_id,
        ...inc.context.flagged_reasons,
      ].join(' ').toLowerCase();
      if (!searchable.includes(q)) return false;
    }
    return true;
  });
}
